/**
 * Plot Drives Column Mapping
 * Maps column numbers (col1, col2, etc.) to semantic field names expected by categories
 */

import { EVALUATE_CODES, EvaluateCode } from './constants';
import { formatDimensionLabel, normaliseDecimalMmInLabel } from './dimensionLabel';

/**
 * Reconstruct plot drives column headers from dimension values.
 * Mirrors the logic in PlotDrives.tsx so bill summary shows the same labels
 * even when PlotDrives hasn't mounted in the current session.
 */
export function reconstructPlotDrivesColumnHeaders(
  columnHeaders: Record<string, string>,
  dimensionValues: Record<string, string>
): Record<string, string> {
  // Normalise any persisted decimal-mm patterns up front (e.g. headers saved
  // before the bill-summary normalisation existed: "0.150mm thick MOT Type 1"
  // → "150mm thick MOT Type 1") so the downstream reconstruction never has to.
  const result: Record<string, string> = {};
  Object.entries(columnHeaders).forEach(([key, value]) => {
    result[key] = normaliseDecimalMmInLabel(value);
  });

  // A persisted header can be just the bare fixed-text tail with no leading
  // dimension at all (e.g. copied from a template/import whose column title
  // has no per-plot depth baked in) — that's not a configured value, it's
  // the unconfigured state, so treat it the same as no header at all rather
  // than displaying it verbatim without its "xx(x)mm thick " placeholder.
  const BARE_FIXED_TEXT_TAILS: Array<[string, string]> = [
    ['col17', '63-10mm sub base - varied depths'],
    ['col36', '63-10mm sub base - varied depths'],
    ['col18', '5-20mm clean aggregate sub base'],
    ['col37', '5-20mm clean aggregate sub base'],
  ];
  BARE_FIXED_TEXT_TAILS.forEach(([colKey, bareText]) => {
    if (result[colKey]?.trim().toLowerCase() === bareText) {
      delete result[colKey];
    }
  });

  // Resolve ALP/GWALPD evaluate codes for any column
  Object.entries(dimensionValues).forEach(([key, value]) => {
    if (value && (value.startsWith('ALP') || value.startsWith('GWALPD'))) {
      const code = value as EvaluateCode;
      if (EVALUATE_CODES[code] && !result[key]) {
        result[key] = EVALUATE_CODES[code].label;
      }
    }
  });

  // Sub base columns → "{value}mm thick MOT Type 1".
  // Decimal entries (e.g. "0.150") are converted to mm for display via
  // `formatDimensionLabel` — applied uniformly across every label-building
  // block below so all decimal stored values render as integer millimetres.
  ['col3', 'col9', 'col22', 'col28'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) {
      // If value is already a full description (e.g. from old import), use it directly
      if (/thick\s+mot\s+type/i.test(value)) {
        result[colKey] = value;
      } else {
        const normalised = formatDimensionLabel(value);
        const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
        result[colKey] = `${v} thick MOT Type 1`;
      }
    }
  });

  // DB Binder Running course columns → value as-is
  ['col16', 'col35'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) result[colKey] = value;
  });

  // A plain "0" means no thickness configured for that field (PlotDrives.tsx
  // deletes the key entirely on clear, but older/imported data can still
  // carry a literal "0") — never build a label/dimension from it, so no
  // fabricated multiplier gets sent downstream for an unconfigured field.
  const isUnconfiguredZero = (value: string) => /^0(\.0+)?$/.test(value);

  // A stored dimension value that's just the bare fixed-text tail (no leading
  // digit at all) is the unconfigured state, not a real entry — e.g. imported
  // verbatim from a template column title with no per-plot depth baked in.
  // Skip it entirely rather than echoing it back with its "xx(x)mm thick "
  // placeholder stripped off.
  const isBareFixedText = (value: string) => !/^\d/.test(value.trim());

  // 63-10mm aggregate sub base columns — treat a plain numeric value (the
  // normal current input shape) the same as 5-20mm below: always build the
  // label from it rather than silently skipping it as "legacy".
  ['col17', 'col36'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey] && !isUnconfiguredZero(value) && !isBareFixedText(value)) {
      const normalised = formatDimensionLabel(value);
      const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
      result[colKey] = value.toLowerCase().includes('varied depths') ? value : `${v} thick 63-10mm Sub Base - Varied Depths`;
    }
  });

  // 5-20mm aggregate sub base columns (skip ALP codes)
  ['col18', 'col37'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey] && !value.startsWith('ALP') && !value.startsWith('GWALPD') && !isUnconfiguredZero(value) && !isBareFixedText(value)) {
      const normalised = formatDimensionLabel(value);
      const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
      result[colKey] = value.toLowerCase().includes('5-20mm') ? value : `${v} thick 5-20mm clean aggregate sub base`;
    }
  });

  // DB Base / DB Binder columns → value as-is
  ['col4', 'col10', 'col11', 'col23', 'col29', 'col5', 'col12', 'col24', 'col30'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) result[colKey] = value;
  });

  // Surface course columns → resolve ALP or use value directly
  ['col50', 'col63', 'col51', 'col52', 'col64', 'col65',
   'col56', 'col57', 'col68', 'col69', 'col60', 'col61', 'col72', 'col73'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) {
      const code = value as EvaluateCode;
      if ((value.startsWith('ALP') || value.startsWith('GWALPD')) && EVALUATE_CODES[code]) {
        result[colKey] = EVALUATE_CODES[code].label;
      } else {
        result[colKey] = value;
      }
    }
  });

  // Laying aggregate columns — always ensure "thick" is present
  ['col59', 'col71'].forEach((colKey) => {
    const existing = result[colKey];
    const dimValue = dimensionValues[colKey];

    if (existing && existing.toLowerCase().includes('2-6mm laying aggregate') && !existing.toLowerCase().includes('thick')) {
      // Existing header is missing "thick" — extract leading dimension and rebuild
      const dimMatch = existing.match(/^(\d+(?:\.\d+)?(?:\.\d+)?)\s*mm/i);
      const fallbackNormalised = dimValue ? formatDimensionLabel(dimValue) : '';
      const dim = dimMatch
        ? `${dimMatch[1]}mm`
        : (fallbackNormalised
            ? (fallbackNormalised.includes('mm') ? fallbackNormalised : `${fallbackNormalised}mm`)
            : 'xxmm');
      result[colKey] = `${dim} thick 2-6mm laying Aggregate`;
    } else if (!existing && dimValue) {
      const normalised = formatDimensionLabel(dimValue);
      const v = normalised.includes('mm') ? normalised : `${normalised}mm`;
      result[colKey] = dimValue.toLowerCase().includes('2-6mm laying aggregate') && dimValue.toLowerCase().includes('thick')
        ? dimValue
        : `${v} thick 2-6mm laying Aggregate`;
    }
  });

  // Sand laying course columns → value as-is
  ['col55', 'col67'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) result[colKey] = value;
  });

  // Kerb columns → "{value} Kerb"
  ['col43', 'col44', 'col45'].forEach((colKey) => {
    const value = dimensionValues[colKey];
    if (value && !result[colKey]) {
      result[colKey] = value.toLowerCase().includes('kerb') ? value : `${value} Kerb`;
    }
  });

  return result;
}

export interface PlotDrivesSectionAlignmentResult {
  rows: Record<string, any>[];
  columnHeaders: Record<string, string>;
  changed: boolean;
}

/**
 * Detect and correct Plot Drives data saved before PlotDrives.tsx's own
 * import-time section-boundary alignment fix existed: each of the 6
 * "Level & Compact" section starts should sit at col1/7/14/20/26/33, but
 * legacy-saved data can have one or more of them shifted left, which also
 * shifts every column after it (so a take-off quantity meant for e.g. col16
 * ends up stored under a lower col key). Mirrors the logic in PlotDrives.tsx's
 * own mount-time alignment effect, so this can be applied to a raw tender's
 * data — e.g. by Configure Headers validation — without PlotDrives.tsx having
 * mounted in the current session first.
 */
export function alignPlotDrivesSectionBoundaries(
  columnHeaders: Record<string, string>,
  rows: Record<string, any>[]
): PlotDrivesSectionAlignmentResult {
  const loadedHeaders: Record<string, string> = { ...columnHeaders };
  const expectedSectionStarts = [1, 7, 14, 20, 26, 33];
  const actualSectionStarts = Object.keys(loadedHeaders)
    .filter((key) => {
      const h = (loadedHeaders[key] || '').toLowerCase();
      return h.includes('level') && h.includes('compact');
    })
    .map((key) => parseInt(key.replace('col', ''), 10))
    .sort((a, b) => a - b)
    .slice(0, 6);

  let dataToAlign = rows.map((row) => ({ ...row }));
  let changed = false;

  for (let i = 0; i < actualSectionStarts.length; i++) {
    const actual = actualSectionStarts[i];
    const expected = expectedSectionStarts[i];
    if (actual < expected) {
      changed = true;
      const slotsToInsert = expected - actual;
      for (let s = 0; s < slotsToInsert; s++) {
        for (let colNum = 77; colNum > actual; colNum--) {
          const destKey = `col${colNum}`;
          const srcKey = `col${colNum - 1}`;
          if (loadedHeaders[srcKey]) {
            loadedHeaders[destKey] = loadedHeaders[srcKey];
          } else {
            delete loadedHeaders[destKey];
          }
        }
        delete loadedHeaders[`col${actual}`];

        dataToAlign = dataToAlign.map((row) => {
          const newRow = { ...row };
          for (let colNum = 77; colNum > actual; colNum--) {
            newRow[`col${colNum}`] = row[`col${colNum - 1}`] ?? 0;
          }
          newRow[`col${actual}`] = 0;
          return newRow;
        });

        for (let j = i; j < actualSectionStarts.length; j++) {
          actualSectionStarts[j]++;
        }
      }
    }
  }

  return { rows: changed ? dataToAlign : rows, columnHeaders: loadedHeaders, changed };
}

export interface PlotDrivesSanitizedConfig {
  dimensionValues: Record<string, string>;
  columnHeaders: Record<string, string>;
}

/**
 * Strip persisted Plot Drives Configure Headers values that PlotDrives.tsx's
 * own mount-time load effect treats as junk/placeholder rather than a real
 * configured value — an invalid DB Base/Binder label saved before the current
 * dropdown options existed, a legacy plain-number aggregate sub-base value
 * saved with no header, a leftover ALP/GWALPD code on col18/37, or an
 * all-"x" kerb placeholder. Mirrors PlotDrives.tsx's own cleanup so a
 * validation run against a tender that hasn't had PlotDrives.tsx mount in
 * the current session sees the same "actually unconfigured" state a visit
 * would leave behind, instead of treating leftover junk as configured.
 */
export function sanitizePlotDrivesConfig(
  dimensionValues: Record<string, string>,
  columnHeaders: Record<string, string>
): PlotDrivesSanitizedConfig {
  const dims = { ...dimensionValues };
  const headers = { ...columnHeaders };

  const validDbBase = new Set(
    Object.values(EVALUATE_CODES)
      .filter((l) => ['ALP041', 'ALP042'].includes(l.code))
      .map((l) => l.label.toLowerCase())
  );
  const validDbBinder = new Set(
    Object.values(EVALUATE_CODES)
      .filter((l) => ['ALP043', 'ALP044', 'ALP045', 'ALP046', 'ALP047'].includes(l.code))
      .map((l) => l.label.toLowerCase())
  );
  const dbBaseCols = ['col4', 'col10', 'col23', 'col29'];
  const dbBinderCols = ['col5', 'col11', 'col24', 'col30'];
  [...dbBaseCols, ...dbBinderCols].forEach((key) => {
    const validSet = dbBaseCols.includes(key) ? validDbBase : validDbBinder;
    const h = (headers[key] || '').toLowerCase().trim();
    if (h && !validSet.has(h)) delete headers[key];
    const d = (dims[key] || '').toLowerCase().trim();
    if (d && !validSet.has(d)) delete dims[key];
  });

  // 63-10mm aggregate sub base: a plain-digit legacy value with no
  // "varied depths" header is pre-migration junk, not a real depth.
  ['col17', 'col36'].forEach((key) => {
    const value = dims[key];
    if (!value) return;
    const header = (headers[key] || '').toLowerCase();
    const isLegacyHeader = header.includes('sub base') && !header.includes('varied depths');
    const isLegacyValue = /^\d+(\.\d+)?$/.test(value) && !header.includes('varied depths');
    if (isLegacyHeader || isLegacyValue) {
      delete dims[key];
      delete headers[key];
    }
  });

  // 5-20mm aggregate sub base: a leftover evaluate code is pre-migration junk
  // (this column used to be a dropdown storing ALP/GWALPD codes).
  ['col18', 'col37'].forEach((key) => {
    const value = dims[key];
    if (value && (value.startsWith('ALP') || value.startsWith('GWALPD'))) {
      delete dims[key];
      delete headers[key];
    }
  });

  // Kerbs: an all-"x" placeholder value is unconfigured, not a real entry.
  ['col43', 'col44', 'col45'].forEach((key) => {
    const value = dims[key];
    if (value && /^x+$/i.test(value.trim())) {
      delete dims[key];
      delete headers[key];
    }
  });

  return { dimensionValues: dims, columnHeaders: headers };
}

export const PLOT_DRIVES_COLUMN_MAPPING: { [colKey: string]: string } = {
  // Private Macadam (cols 1-6, 48-52)
  'col1': 'macadamLevelCompact',
  'col2': 'macadamGeotextileLayer',
  'col3': 'macadamSubBase',
  'col4': 'macadamDbBase',
  'col5': 'macadamDbBinder',
  'col6': 'macadamSealingGrit',
  'col48': 'macadamRemoveTimberEdgings',
  'col49': 'macadamTack',
  'col50': 'macadamDbSurfaceCourse',
  'col51': 'macadamHraSurfaceCourse',
  'col52': 'macadamSmaSurfaceCourse',

  // Private Block Paved (cols 7-12, 53-57)
  'col7': 'blockLevelCompact',
  'col8': 'blockGeotextileLayer',
  'col9': 'block300mmAdditionalCapping',
  'col10': 'block250mmSubBase',
  'col11': 'block100mmDbBase',
  'col12': 'blockSealingGrit',
  'col53': 'blockSweep',
  'col54': 'blockPunctureMacadam',
  'col55': 'blockLayingCourse',
  'col56': 'block60mmConcrete',
  'col57': 'block80mmConcrete',

  // Permeable Paving (cols 14-19, 58-61)
  'col14': 'permeableLevelCompact',
  'col15': 'permeableImpermeableGeotextileLayer',
  'col16': 'permeableDbBinderRunningCourse',
  'col17': 'permeable10to63mmCleanAggregateSubBase',
  'col18': 'permeable5to20mmCleanAggregateSubBase',
  'col19': 'permeableGeotextileLayer',
  'col58': 'permeablePunctureMacadam',
  'col59': 'permeable2to6mmLayingAggregate',
  'col60': 'permeable60mmConcrete',
  'col61': 'permeable80mmConcrete',

  // Shared Macadam (cols 20-25, 62-65)
  'col20': 'sharedMacadamLevelCompact',
  'col21': 'sharedMacadamGeotextileLayer',
  'col22': 'sharedMacadamSubBase',
  'col23': 'sharedMacadamDbBase',
  'col24': 'sharedMacadamDbBinder',
  'col25': 'sharedMacadamSealingGrit',
  'col62': 'sharedMacadamSweepTack',
  'col63': 'sharedMacadamDbSurfaceCourse',
  'col64': 'sharedMacadamHraSurfaceCourse',
  'col65': 'sharedMacadamSmaSurfaceCourse',

  // Shared Block Paved (cols 26-31, 66-69)
  'col26': 'sharedBlockLevelCompact',
  'col27': 'sharedBlockGeotextileLayer',
  'col28': 'sharedBlockSubBase',
  'col29': 'sharedBlockBase',
  'col30': 'sharedBlockBinder',
  'col31': 'sharedBlockSealingGrit',
  'col66': 'sharedBlockPunctureMacadam',
  'col67': 'sharedBlockLayingCourse',
  'col68': 'sharedBlock60mmConcrete',
  'col69': 'sharedBlock80mmConcrete',

  // Shared Permeable (cols 33-47, 70-77)
  'col33': 'sharedPermeableLevelCompact',
  'col34': 'sharedPermeableImpermeableGeotextileLayer',
  'col35': 'sharedPermeableDbBinderRunningCourse',
  'col36': 'sharedPermeable10to63mmCleanAggregateSubBase',
  'col37': 'sharedPermeable5to20mmCleanAggregateSubBase',
  'col38': 'sharedPermeableGeotextileLayer',
  'col39': 'sharedPermeable150x50mmPccEdgings',
  'col40': 'sharedPermeable200x50mmPccEdgings',
  'col41': 'sharedPermeableTimberEdgings',
  'col42': 'sharedPermablePccKerbs',
  'col43': 'sharedPermeableKerbType1',
  'col44': 'sharedPermeableKerbType2',
  'col45': 'sharedPermeableKerbType3',
  'col46': 'sharedPermeableKeyKerb',
  'col47': 'sharedPermeableRecessedCF',
  'col70': 'sharedPermeablePunctureMacadam',
  'col71': 'sharedPermeable2to6mmLayingAggregate',
  'col72': 'sharedPermeable60mmConcrete',
  'col73': 'sharedPermeable80mmConcrete',
  'col74': 'sharedPermeableAdjustIronwork',
  'col75': 'sharedPermeableDriveDemo',
  'col76': 'sharedPermeableEdgingRemedials',
  'col77': 'sharedPermeableKerbRemedials',
};

/**
 * Reverse mapping: semantic field name to column key
 * Used for mapping dimension values back to their column keys
 */
export const PLOT_DRIVES_REVERSE_MAPPING: { [semanticName: string]: string } = {};
Object.entries(PLOT_DRIVES_COLUMN_MAPPING).forEach(([colKey, semanticName]) => {
  if (semanticName) {
    PLOT_DRIVES_REVERSE_MAPPING[semanticName] = colKey;
  }
});

/**
 * Transform Plot Drives data from column format to semantic field format
 */
export function transformPlotDrivesData(plotDrivesTable: any[]): any[] {
  if (!plotDrivesTable || !Array.isArray(plotDrivesTable)) {
    return [];
  }

  return plotDrivesTable.map(row => {
    const transformedRow: any = {
      key: row.key,
      plotNo: row.plotNo,
      housetype: row.housetype,
    };

    // Transform all column fields to semantic field names
    Object.keys(row).forEach(colKey => {
      if (PLOT_DRIVES_COLUMN_MAPPING[colKey]) {
        const semanticFieldName = PLOT_DRIVES_COLUMN_MAPPING[colKey];
        transformedRow[semanticFieldName] = row[colKey];
      } else if (!['key', 'plotNo', 'housetype'].includes(colKey)) {
        // Keep unmapped columns as-is (for debugging)
        transformedRow[colKey] = row[colKey];
      }
    });

    return transformedRow;
  });
}